import { useEffect, useState } from "react";
import { useLocale } from "../lib/LocaleProvider";

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

function Meter({ title, done, total, children, count, text }) {
  const known = Number.isInteger(done) && Number.isInteger(total) && total > 0;
  const percent = known ? Math.min(100, Math.floor(done / total * 100)) : null;
  return (
    <section className="work-progress-meter">
      <div className="work-progress-meter-heading">
        <h3>{title}</h3>
        <span>{known ? `${count(done)} / ${count(total)}` : text("workProgress.noCount")}</span>
      </div>
      {known && <progress max={total} value={Math.min(done, total)} aria-label={title} />}
      <div className="work-progress-meter-note">
        <span>{known ? text("workProgress.processed", { percent: count(percent) }) : text("workProgress.awaitingCount")}</span>
        {children}
      </div>
    </section>
  );
}

export default function WorkProgress() {
  const { text, count, languageTag } = useLocale();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(() => {
    try { return window.localStorage.getItem("invictus-work-progress") !== "closed"; }
    catch { return true; }
  });
  const [disconnected, setDisconnected] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const local = LOCAL_HOSTS.has(window.location.hostname);

  useEffect(() => {
    if (!local) return undefined;
    let cancelled = false;
    let supported = true;
    let timer;
    const controller = new AbortController();
    async function refresh() {
      try {
        const response = await fetch("/api/local/work-status", { cache: "no-store", signal: controller.signal });
        if (response.status === 404) { supported = false; if (!cancelled) setUnsupported(true); return; }
        if (!response.ok) throw new Error("status unavailable");
        const next = await response.json();
        if (!cancelled) { setData(next); setDisconnected(false); }
      } catch {
        if (!cancelled) setDisconnected(true);
      } finally {
        if (!cancelled && supported) timer = window.setTimeout(refresh, 15000);
      }
    }
    refresh();
    return () => { cancelled = true; controller.abort(); window.clearTimeout(timer); };
  }, [local]);

  if (!local || unsupported) return null;
  if (!data) return <aside className="work-progress" aria-label={text("workProgress.title")}>
    <div className="work-progress-toggle" role="status"><span><strong>{text("workProgress.title")}</strong>
      <small>{text(disconnected ? "workProgress.connection" : "workProgress.connecting")}</small></span></div>
  </aside>;
  const state = disconnected ? "disconnected" : data.state;
  const stage = data.stage ? text(`workProgress.stages.${data.stage}`) : text(`workProgress.states.${state}`);
  const timestamp = new Date(data.checked_at).toLocaleTimeString(languageTag, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  function toggle() {
    setOpen(!open);
    try { window.localStorage.setItem("invictus-work-progress", open ? "closed" : "open"); } catch { /* Optional preference. */ }
  }
  return (
    <aside className={`work-progress ${open ? "is-open" : ""}`} aria-label={text("workProgress.title")}>
      <button className="work-progress-toggle" onClick={toggle} aria-expanded={open} aria-controls="work-progress-content">
        <span className={`work-progress-indicator state-${state}`} aria-hidden="true" />
        <span><strong>{text("workProgress.title")}</strong><small>{stage}</small></span>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d={open ? "m6 15 6-6 6 6" : "m6 9 6 6 6-6"} /></svg>
      </button>
      {open && <div id="work-progress-content" className="work-progress-content">
        <div className="work-progress-state" role="status" aria-live="polite">
          <strong>{text(`workProgress.states.${state}`)}</strong>
          <time dateTime={data.checked_at}>{text("workProgress.updatedAt", { time: timestamp })}</time>
        </div>
        {state === "attention" && <p className="work-progress-notice">{text("workProgress.attention")}</p>}
        {state === "disconnected" && <p className="work-progress-notice">{text("workProgress.connection")}</p>}
        <Meter title={text("workProgress.ocr")} done={data.ocr.processed} total={data.ocr.total} {...{ count, text }}>
          {data.ocr.ready != null && <span>{text("workProgress.readyPages", { ready: count(data.ocr.ready), held: count(data.ocr.held) })}</span>}
          {data.ocr.awaiting_check > 0 && <span>{text("workProgress.awaitingCheck", { count: count(data.ocr.awaiting_check) })}</span>}
        </Meter>
        <Meter title={text("workProgress.translation")} done={data.translation.processed} total={data.translation.total} {...{ count, text }}>
          <span>{text("workProgress.translationResults", { passed: count(data.translation.passed), held: count(data.translation.held) })}</span>
        </Meter>
        <p className="work-progress-explanation">{text("workProgress.scope")}</p>
        {data.translation.blocked > 0 && <p className="work-progress-blocked">{text("workProgress.blocked", { count: count(data.translation.blocked) })}</p>}
        {data.translation.awaiting_alignment > 0 && <p className="work-progress-explanation">{text("workProgress.awaitingAlignment", { count: count(data.translation.awaiting_alignment) })}</p>}
        {data.ocr.measured_at && <p className="work-progress-audit-time">{text("workProgress.qualityAt", { date: new Date(data.ocr.measured_at).toLocaleString(languageTag) })}</p>}
        <ol className="work-progress-steps" aria-label={text("workProgress.steps")}>
          {data.steps.map(step => <li key={step.name} className={`is-${step.state}`} aria-current={step.state === "active" ? "step" : undefined}>
            <span className="work-progress-step-mark" aria-hidden="true">{step.state === "done" && <svg viewBox="0 0 24 24"><path d="m5 12 4 4 10-10" /></svg>}</span>
            {text(`workProgress.stages.${step.name}`)}
            <small>{text(`workProgress.stepStates.${step.state}`)}</small>
          </li>)}
        </ol>
      </div>}
    </aside>
  );
}
